import { UTCDate } from '@date-fns/utc';
import {
  useRecentOperationLogData,
  useUndoOperationLog,
} from '@ops/entities/operation-log';
import { toastMutation } from '@ops/shared/lib/toast-mutation';
import { useNavigate } from '@tanstack/react-router';
import { isReversibleOperationLogEntryType } from '@travel-planner/shared/schemas/operation-log';
import { ChevronDown, History, Undo2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getInitials } from '@/shared/lib/get-initials';
import { Avatar, Button, Skeleton, Text } from '@/shared/ui';
import { describeLogEntry, timeAgo } from '../lib/describe-log-entry';
import css from './recent-changes-panel.module.scss';

export const RecentChangesPanel = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const recent = useRecentOperationLogData(open);
  const undo = useUndoOperationLog();
  const entries = recent.data?.data ?? [];
  // Fixed reference point per open so every row's relative time is consistent.
  const nowMs = useRef(0);
  if (open && nowMs.current === 0) nowMs.current = new UTCDate().getTime();
  if (!open && nowMs.current !== 0) nowMs.current = 0;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleUndo = (logId: string) =>
    undo.mutate(
      { logId },
      toastMutation({ success: 'Change reverted', error: 'Couldn’t undo' })
    );

  return (
    <div className={css.root} ref={rootRef}>
      <Button
        aria-expanded={open}
        icon={<History size={15} />}
        onClick={() => setOpen((o) => !o)}
        variant="outline"
      >
        Recent changes
        <ChevronDown size={14} />
      </Button>

      {open && (
        <div className={css.panel}>
          <div className={css.header}>
            <span className={css.title}>Recent changes</span>
            <span className={css.muted}>Last 24h</span>
          </div>

          {recent.isLoading ? (
            Array.from({ length: 3 }, (_, i) => (
              <div className={css.entry} key={i}>
                <Skeleton height={28} variant="circular" width={28} />
                <div className={css.entryBody}>
                  <Skeleton width="85%" />
                  <Skeleton width="40%" />
                </div>
              </div>
            ))
          ) : entries.length === 0 ? (
            <div className={css.state}>
              <Text size="xs" variant="muted">
                No changes in the last 24 hours.
              </Text>
            </div>
          ) : (
            entries.map((entry) => (
              <div className={css.entry} key={entry.id}>
                <Avatar
                  initials={getInitials(entry.createdByName ?? '—')}
                  size="sm"
                />
                <div className={css.entryBody}>
                  <span className={css.desc}>{describeLogEntry(entry)}</span>
                  <div className={css.meta}>
                    <span className={css.time}>
                      {timeAgo(entry.createdOn, nowMs.current)}
                    </span>
                    {isReversibleOperationLogEntryType(entry.entryType) && (
                      <button
                        className={css.undo}
                        disabled={undo.isPending}
                        onClick={() => handleUndo(entry.id)}
                        type="button"
                      >
                        <Undo2 size={11} />
                        Undo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          <button
            className={css.viewAll}
            onClick={() => {
              setOpen(false);
              navigate({ to: '/operations' });
            }}
            type="button"
          >
            View all activity
          </button>
        </div>
      )}
    </div>
  );
};
